import {data, onChanges} from "@@context";
import {findRoots} from "../utils/dom";
import {warn} from "../utils/log";
import {getLanguage, translate} from "../utils/i18n";
import {markSolutionAsNotFresh} from "../pow/validate";
import {HIDDEN_FIELD_NAME} from "../utils/constants";
import {getErrorMessage, getTokenFromSolution} from "./util";

const DISABLED = 'data-swss-disabled';
const HIDDEN_FIELD_SELECTOR = `input[type=hidden][name="${HIDDEN_FIELD_NAME}"],input[type=hidden][name$="[${HIDDEN_FIELD_NAME}]"]`;
const SUBMIT_BUTTON_SELECTOR = "input[type=submit],input[type=image],button[type=submit],button:not([type])";
const SELECTORS = [HIDDEN_FIELD_SELECTOR, SUBMIT_BUTTON_SELECTOR].join(',')

const shadows = new WeakSet<ShadowRoot | Document>()
let needsShadowDiscovery = true;
const forms = new WeakSet<HTMLFormElement>();
const submitButtons = new Set<HTMLElement>();
const hiddenFields = new Set<HTMLInputElement>();
let discoverNewElements: MutationObserver;
let readyCalled = false;
let syncButtons = false;

onChanges('solution', () => {
    const solution = data.solution;
    hiddenFields.forEach(f => sync(f, getTokenFromSolution(f, solution)));
})

export function ready() {
    if (readyCalled) {
        return;
    }
    readyCalled = true;
    const integrations: (Promise<typeof import('./brevo')>)[] = []
    if (document.body.classList.contains('__swss-integration-brevo')) {
        integrations.push(import('./brevo'))
    }
    if (document.body.classList.contains('__swss-integration-tnp')) {
        integrations.push(import('./the-newsletter-plugin'))
    }
    if (document.body.classList.contains('__swss-integration-enfold')) {
        integrations.push(import('./enfold'))
    }
    if (document.body.classList.contains('__swss-integration-ninjaforms')) {
        integrations.push(import('./ninjaforms'))
    }
    Promise.all(integrations).then((modules) => {modules.forEach(module => module.load())})
    solveChallenges(document);
    if (typeof MutationObserver === 'function') {
        discoverNewElements = (new MutationObserver(mutations => mutations.map(m => m.target).forEach(solveChallenges)));
        discoverNewElements.observe(document.body, {
            childList: true,
            subtree: true
        })
    }
}

// Periodically discover new shadow roots and observe them
// We do this asynchronously because this walks the whole DOM tree (including shadow roots) and can be expensive
function discoverNewShadowRoots() {
    if (discoverNewElements && needsShadowDiscovery) {
        needsShadowDiscovery = false;
        findRoots(document.body).forEach((root) => {
            if (!shadows.has(root)) {
                shadows.add(root);
                discoverNewElements.observe(root, {
                    childList: true,
                    subtree: true
                })
            }
        })
    }
    setTimeout(discoverNewShadowRoots, 1000)
}

setTimeout(discoverNewShadowRoots, 1000)

function sync(element: HTMLInputElement, token: string) {
    // prevent memory leaks by removing the element from the set if it is no longer connected
    if (!element.isConnected) {
        hiddenFields.delete(element);
        return;
    }
    if (element.value !== token) {
        element.setAttribute('value', token);
    }
    if (token && !syncButtons) {
        syncButtons = true;
        queueMicrotask(() => {
            syncButtons = false;
            const hasSolution = !!data.solution;
            submitButtons.forEach((button) => syncButton(button, hasSolution));
        })
    }
}

function syncButton(button: HTMLElement, hasSolution: boolean) {
    if (!button.isConnected) {
        button.removeAttribute(DISABLED);
        submitButtons.delete(button);
    } else {
        if (hasSolution) {
            button.removeAttribute(DISABLED);
        } else {
            if (!button.hasAttribute(DISABLED)) {
                button.setAttribute(DISABLED, DISABLED);
            }
        }
    }
}

const solveChallenges = window.__swSolveChallenges = function solveChallenges(root: Node | Document | Element): void {
    // Support HTML Fragments and ShadowRoot - for the public API window.__swSolveChallenges
    if (!('querySelectorAll' in root) && root instanceof Node && !(root instanceof Element)) {
        root.childNodes.forEach(solveChallenges);
        return
    }
    // in the next shadow root discovery run, we will actually do something.
    // when a shadow root gets initially discovered, this gets called twice - but we do not care
    needsShadowDiscovery = true

    const solution = data.solution || '';
    ;('querySelectorAll' in root ? root : document).querySelectorAll(SELECTORS).forEach(function (element) {
        // this is a __swss_pow__ hidden field?
        if (element instanceof HTMLInputElement && element.matches(HIDDEN_FIELD_SELECTOR) && !hiddenFields.has(element)) {
            hiddenFields.add(element);
            sync(element, getTokenFromSolution(element, solution))
        }
        // Discover relevant forms by the form reference of submit buttons and the __swss_pow__ hidden field
        // This should support buttons that are outside ot=f their form (<button form="...">)
        if ('form' in element && element.form instanceof HTMLFormElement && !!element.form.querySelector(HIDDEN_FIELD_SELECTOR)) {
            if (!forms.has(element.form)) {
                forms.add(element.form);
                element.form.addEventListener("submit", function (e: SubmitEvent): void {
                    const form = e.target as HTMLFormElement;
                    const validateResult = validateForm(form);
                    if (validateResult !== true) {
                        e.preventDefault();
                        e.stopPropagation();
                        queueMicrotask(() => alert(validateResult));
                        return;
                    }
                });
            }
            if (element.matches(SUBMIT_BUTTON_SELECTOR) && element instanceof HTMLElement && !submitButtons.has(element)) {
                submitButtons.add(element);
                syncButton(element, !!solution);
            }
        }
    })
}

const validateForm = window.__swssValidateForm = function validateForm(form: HTMLFormElement): true | string {
    let valid = false;
    let found = false;
    for (const response of form.querySelectorAll(HIDDEN_FIELD_SELECTOR)) {
        found = true;
        if (data.solution && 'value' in response && typeof response.value == 'string' && response.value.endsWith(data.solution)) {
            valid = true;
            break;
        }
    }
    if (found && !valid) {
        warn("Form submission prevented.");
        const lang = getLanguage(form);
        if (data.state === 'error') {
            const msg = getErrorMessage(lang) || ''
            return translate(lang, 'form.submit-with-error', {msg})
        } else if (data.state === 'validating' || data.state === 'solving') {
            return translate(lang, 'form.still-solving')
        } else {
            return translate(lang, 'form.submit-without-token');
        }
    } else if (found) {
        markSolutionAsNotFresh()
    }
    return true
}
